/**
 * Getting somewhere on its own: the controls a traveller would use to follow
 * a route — by car, keeping right and within the road's speed, slowing into
 * bends and junctions and stopping before turning at one; on foot or on the
 * motorbike, along paths and pavements; by helicopter, straight there.
 *
 * Each step takes where the traveller is and how it is moving, and returns
 * the stick and pedals: the same inputs the keyboard gives, so the machine
 * drives exactly as it would under a player's hands.
 *
 * By car it drives among the traffic (`TrafficSense`): in the middle of a
 * real lane rather than a guess to the right of the road's line, keeping its
 * distance from the car ahead down to a stop behind it, pulling out past a
 * slower one where there is a lane to do it in and back in after, and at a
 * line it stops at, waiting until nothing is coming.
 *
 * Pure: no Three, no DOM.
 */

import type { Route } from './route.js';
import { bendAhead, pointAt, project } from './route.js';
import { comingThrough } from '../traffic/lanes.js';

export type Travel = 'car' | 'moto' | 'foot' | 'heli';

/** Where the traveller is and how it moves, in the route's frame. */
export interface Pose {
  east: number;
  north: number;
  /** Compass heading, radians clockwise from north. */
  heading: number;
  /** Forward speed, m/s. */
  speed: number;
}

export interface GroundControls {
  /** Pedal: 1 full throttle .. -1 full brake. */
  throttle: number;
  /** -1 left .. 1 right. */
  steer: number;
  /** On foot: which way to run, unit east and north. */
  moveEast: number;
  moveNorth: number;
  done: boolean;
  /** The speed it is aiming for, m/s: for the display. */
  target: number;
}

/** Another vehicle on the road, in the route's frame. */
export interface Other {
  east: number;
  north: number;
  /** Unit direction it is going, east and north. */
  de: number;
  dn: number;
  speed: number;
  halfLength: number;
}

/** What the traffic around the car is, and where its lanes run, in the route's frame. */
export interface TrafficSense {
  others: readonly Other[];
  /**
   * The middle of the lane to drive in near (east, north), going (de, dn):
   * the rightmost lane that way, or with `left` the one beside it. Whether
   * there is a lane to its left. Undefined where no traffic lane is mapped.
   */
  lane(east: number, north: number, de: number, dn: number, left: boolean): { east: number; north: number; hasLeft: boolean } | undefined;
}

/** Braking comfortably, m/s²: well under what the car can. */
const EASY_BRAKE = 2.6;
/** Sideways acceleration taken through a bend, m/s². */
const BEND_GRIP = 3.2;
/** Through a junction it gives way at, m/s (25 km/h). */
const JUNCTION_SPEED = 7;
/** Stop this far before a junction to be turned at, metres: the stop line. */
const STOP_BEFORE = 7;
/** Standing at a stop line, seconds, before going. */
const STOP_WAIT = 1.2;
/** How far right of the road's middle a car keeps, metres. */
const KEEP_RIGHT = 1.7;
/** Braking for the car ahead, m/s²: firmer than for a bend, still no emergency. */
const FOLLOW_BRAKE = 3.5;
/** Bumper to bumper behind a stopped car, metres, and the time gap kept at speed, seconds. */
const STANDING_GAP = 3.5;
const HEADWAY = 1.4;
/** Lanes side by side are this far apart; a car within this much of the line driven is in the way. */
const LANE_WIDTH = 3.5;
const IN_LANE = 1.8;
/** How much slower the car ahead must be to pass it, m/s, and the least time between lane changes, seconds. */
const PASS_FOR = 3;
const LANE_SETTLE = 4;
/** At a line, the longest it waits for a gap before taking its turn anyway, seconds. */
const GAP_PATIENCE = 15;
/** The fastest the car is taken, m/s (120 km/h): Sweden's highest limit. */
const CAR_MAX = 33.4;
/** Riding the motorbike along paths and pavements, m/s: a brisk ride, not a race. */
const MOTO_SPEED = 9;

/** Follows a route: what it has passed, and what it is waiting at. */
export class Follower {
  /** Metres along the route reached. */
  along = 0;
  /** Junctions already stopped at or passed, by index. */
  private next = 0;
  private waited = 0;
  /** Out in the lane to the left, passing. */
  private passing = false;
  private sinceLaneChange = Infinity;
  /** Waiting at a line for a gap in the traffic, seconds. */
  private waitedForGap = 0;

  constructor(readonly route: Route, readonly travel: Travel) {}

  /** The route's junctions were replaced: find its place among the new ones. */
  junctionsChanged(): void {
    this.next = 0;
    this.waited = 0;
  }

  /** The controls for a traveller on the ground, for `dt` seconds, among `traffic` if there is any. */
  ground(pose: Pose, dt: number, traffic?: TrafficSense): GroundControls {
    const route = this.route;
    // Where along it: near where it was, so a route doubling back is followed in order.
    const at = project(route, pose.east, pose.north, this.along - 5, this.along + 60);
    if (at.off < 200) this.along = Math.max(this.along, at.along);
    const left = route.length - this.along;
    const done = left < (this.travel === 'car' ? 4 : 1.5) && pose.speed < 0.6;
    const out: GroundControls = { throttle: 0, steer: 0, moveEast: 0, moveNorth: 0, done, target: 0 };
    if (done) return out;

    if (this.travel === 'foot') {
      const aim = pointAt(route, this.along + 2.5);
      const de = aim.east - pose.east, dn = aim.north - pose.north, d = Math.hypot(de, dn) || 1;
      out.moveEast = de / d;
      out.moveNorth = dn / d;
      out.target = 6;
      return out;
    }

    // Pure pursuit: steer for a point ahead on the route, further the faster.
    const car = this.travel === 'car';
    const sense = car ? traffic : undefined;
    this.sinceLaneChange += dt;
    const ahead = Math.max(car ? 6 : 4, Math.min(30, 3 + pose.speed * 0.9));
    const aim = pointAt(route, this.along + ahead);
    // In the middle of the lane it means to be in, where the lanes are known;
    // else right of the road's middle, by the right-hand normal of its direction.
    const inLane = sense?.lane(aim.east, aim.north, aim.de, aim.dn, this.passing);
    if (inLane === undefined && this.passing) this.passing = false;
    const lane = car ? KEEP_RIGHT : 0;
    const ae = inLane?.east ?? aim.east + aim.dn * lane, an = inLane?.north ?? aim.north - aim.de * lane;
    const want = Math.atan2(ae - pose.east, an - pose.north);
    const error = Math.atan2(Math.sin(want - pose.heading), Math.cos(want - pose.heading));
    out.steer = Math.max(-1, Math.min(1, error * (car ? 2.2 : 2.6)));

    // How fast: the road's own speed, eased for bends, junctions and the end.
    const here = pointAt(route, this.along);
    const road = route.speeds[here.stretch];
    let target = car ? Math.min(CAR_MAX, Number.isFinite(road) ? road : 13) : MOTO_SPEED;
    const bend = bendAhead(route, this.along + 2, 25);
    if (bend > 0.05) target = Math.min(target, Math.sqrt(BEND_GRIP * (25 / bend)));
    // Arrive gently.
    target = Math.min(target, Math.sqrt(2 * EASY_BRAKE * Math.max(0, left - 2)));

    if (car) target = this.junctions(target, pose, dt, sense);
    if (sense !== undefined) target = this.amongTraffic(target, pose, sense, inLane ?? { east: ae, north: an }, aim);
    out.target = target;
    // Pedal from the speed error; never into reverse from a stop.
    const err = target - pose.speed;
    out.throttle = Math.max(-1, Math.min(1, err * 0.5));
    if (target < 0.3 && pose.speed < 0.5) out.throttle = 0;
    // Pointing well away from the route — at the start, off the road — creep round.
    if (Math.abs(error) > 1.2) { out.target = Math.min(target, 3); out.throttle = Math.min(out.throttle, (3 - pose.speed) * 0.5); }
    return out;
  }

  /**
   * Where another vehicle is against the route: how far along it, and how
   * far right of its line (negative left).
   */
  private place(o: { east: number; north: number }): { along: number; right: number; de: number; dn: number } {
    const at = project(this.route, o.east, o.north, this.along - 30, this.along + 120);
    const p = pointAt(this.route, at.along);
    return { along: at.along, right: (o.east - p.east) * p.dn - (o.north - p.north) * p.de, de: p.de, dn: p.dn };
  }

  /**
   * Keep behind the car ahead in the lane it is driving, and pass it where
   * there is a lane to: the speed to aim for, at most `target`.
   */
  private amongTraffic(target: number, pose: Pose, sense: TrafficSense, lane: { east: number; north: number }, aim: { de: number; dn: number }): number {
    // The lines it is on now and means to be on, as offsets from the route's.
    const own = this.place(pose).right;
    const planned = this.place(lane).right;
    // The first thing in the way: in the lane it is in or moving into, ahead
    // along the route. Going its way or across it, it counts at its speed
    // along the route; one coming the other way, as standing.
    const firstIn = (right: number, from = 0) => {
      let gap = Infinity, speed = 0;
      for (const o of sense.others) {
        const at = this.place(o);
        const ahead = at.along - this.along;
        if (ahead <= from || ahead > 100 || Math.abs(at.right - right) > IN_LANE) continue;
        const g = ahead - 2.4 - o.halfLength;
        if (g < gap) { gap = g; speed = Math.max(0, o.speed * (o.de * at.de + o.dn * at.dn)); }
      }
      return { gap, speed };
    };
    const now = firstIn(own), then = firstIn(planned);
    // Pulling out, the car being passed only counts if it is close enough to hit.
    const lead = this.passing && now.gap > STANDING_GAP + 2 ? then : now.gap < then.gap ? now : then;
    // As fast as it can be and still stop behind it, keeping its time gap.
    const keep = STANDING_GAP + HEADWAY * pose.speed;
    const behind = Math.sqrt(Math.max(0, lead.speed ** 2 + 2 * FOLLOW_BRAKE * (lead.gap - keep)));
    const limit = Math.min(target, behind);

    // Pass a slower car where a lane to the left goes this way, out of the
    // way of junctions and bends, and come back in once past.
    const here = sense.lane(pose.east, pose.north, aim.de, aim.dn, false);
    const nextJunction = this.route.junctions.find(j => j.at > this.along)?.at ?? Infinity;
    const calm = nextJunction - this.along > 250 && bendAhead(this.route, this.along, 120) < 0.35;
    if (this.sinceLaneChange > LANE_SETTLE) {
      const right = firstIn(own + LANE_WIDTH, -12);
      if (!this.passing && here?.hasLeft && calm && lead.gap < 60 && lead.speed < target - PASS_FOR &&
        this.clear(sense, own - LANE_WIDTH, pose.speed)) {
        this.passing = true;
        this.sinceLaneChange = 0;
      } else if (this.passing && (!calm || (right.gap > 50 || right.speed > target - 1) && this.clear(sense, own + LANE_WIDTH, pose.speed))) {
        this.passing = false;
        this.sinceLaneChange = 0;
      }
    }
    return limit;
  }

  /** Whether the lane at `right` of the route's line has room alongside, from behind to ahead, at `speed`. */
  private clear(sense: TrafficSense, right: number, speed: number): boolean {
    for (const o of sense.others) {
      const at = this.place(o);
      if (Math.abs(at.right - right) > IN_LANE) continue;
      const ahead = at.along - this.along;
      const closing = speed - o.speed;
      // Room ahead to pull in front of it, and behind for one coming up faster.
      if (ahead > -(8 + Math.max(0, -closing) * 3) && ahead < 10 + Math.max(0, closing) * 2.5) return false;
    }
    return true;
  }

  /** Slow for the junctions it gives way at, and stop at the line where it must, until there is a gap. */
  private junctions(target: number, pose: Pose, dt: number, sense?: TrafficSense): number {
    const js = this.route.junctions;
    while (this.next < js.length && js[this.next].at < this.along - 1) { this.next++; this.waited = 0; this.waitedForGap = 0; }
    for (let k = this.next; k < js.length; k++) {
      const j = js[k];
      const to = j.at - this.along;
      if (to > 120) break;
      if (j.stop && (this.waited < STOP_WAIT || this.coming(j.at, sense))) {
        const line = to - STOP_BEFORE;
        if (line < 1.5 && pose.speed < 0.4) {
          // At the line: wait, look, and go when nothing is coming.
          this.waited += dt;
          if (this.waited >= STOP_WAIT) this.waitedForGap += dt;
          return 0;
        }
        target = Math.min(target, Math.sqrt(2 * EASY_BRAKE * Math.max(0, line)));
      } else {
        target = Math.min(target, Math.sqrt(JUNCTION_SPEED ** 2 + 2 * EASY_BRAKE * Math.max(0, to - 3)));
      }
    }
    return target;
  }

  /** Whether anything is coming through the junction `at` metres along the route soon: a car to let by. */
  private coming(at: number, sense?: TrafficSense): boolean {
    if (sense === undefined || this.waitedForGap > GAP_PATIENCE) return false;
    const j = pointAt(this.route, at);
    // A car standing still is queueing, or waiting at its own line, perhaps for this one.
    return sense.others.some(o => o.speed > 0.5 && comingThrough(o.east - j.east, o.north - j.north, o.de, o.dn, o.speed));
  }
}

/** A helicopter going straight to a point and landing there. */
export interface HeliPose {
  /** Metres east and north of the destination. */
  east: number;
  north: number;
  /** Height over the ground under it, metres. */
  agl: number;
  heading: number;
  /** Velocity, m/s: east, north, up. */
  ve: number;
  vn: number;
  vu: number;
  landed: boolean;
}

export interface HeliControls {
  cyclicPitch: number;
  cyclicRoll: number;
  yaw: number;
  collective: number;
  done: boolean;
}

/** Height over the ground to cruise at, metres. */
const CRUISE_AGL = 120;
/** Fastest it is flown, m/s (about 200 km/h). */
const CRUISE_SPEED = 55;

/**
 * Up to cruising height, round to face the destination, forward at a speed
 * that falls off as it nears, and down onto it once over it and slow.
 */
export function flyTo(p: HeliPose, started: boolean): HeliControls {
  const dist = Math.hypot(p.east, p.north);
  const out: HeliControls = { cyclicPitch: 0, cyclicRoll: 0, yaw: 0, collective: 0, done: false };
  if (p.landed && dist < 25 && started) { out.done = true; return out; }
  const bearing = Math.atan2(-p.east, -p.north);
  const turn = Math.atan2(Math.sin(bearing - p.heading), Math.cos(bearing - p.heading));
  // Speed along and across the heading.
  const fwd = p.ve * Math.sin(p.heading) + p.vn * Math.cos(p.heading);
  const side = p.ve * Math.cos(p.heading) - p.vn * Math.sin(p.heading);
  const arriving = dist < 30;
  const climbing = !arriving && p.agl < CRUISE_AGL * 0.6;
  // Face it, unless over it already.
  out.yaw = arriving ? 0 : Math.max(-1, Math.min(1, turn * 1.5));
  // Forward only once climbing clear and facing it.
  const want = arriving ? 0 : climbing || Math.abs(turn) > 0.5 ? Math.min(8, dist * 0.1) : Math.min(CRUISE_SPEED, 2 + dist * 0.09);
  out.cyclicPitch = Math.max(-0.7, Math.min(0.9, (want - fwd) * 0.09));
  out.cyclicRoll = Math.max(-0.4, Math.min(0.4, -side * 0.08));
  // Height: cruise, then down when over the spot and slow.
  const settle = arriving && Math.hypot(p.ve, p.vn) < 3;
  const climb = settle ? Math.max(-4, -0.08 * p.agl - 0.8) : Math.max(-4, Math.min(6, (CRUISE_AGL - p.agl) * 0.25));
  out.collective = Math.max(-1, Math.min(1, (climb - p.vu) * 0.45));
  return out;
}
